/**
 * client/src/components/error-boundary.tsx
 *
 * A small, reusable React error boundary. React unmounts a whole subtree when a
 * descendant throws during render/commit — with no boundary, a single bad
 * component (e.g. a hook iterating a non-array API payload) blanks the ENTIRE
 * app. Wrap a section in this to contain a render throw to a readable fallback
 * instead of a white/blank screen.
 *
 * Colour (CLAUDE.md): the fallback uses amber (attention), never red — red is
 * reserved for Forensic Pause + critical OSINT only.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { describeError } from "@/lib/error-copy";

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  /**
   * Fallback UI. Either a node, or a render function receiving the caught error
   * and a `reset` callback that clears the error state (e.g. a "Try again"
   * button). Omitted → a default cause-specific card (`error-copy.ts`'s
   * `describeError`) with "Try again" and "Reload page" controls.
   */
  readonly fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  /** Optional label so the default fallback names the failed area ("the dashboard"). */
  readonly label?: string;
  /** Optional hook for logging/telemetry beyond the console. */
  readonly onError?: (error: Error, info: ErrorInfo) => void;
  /**
   * When this value changes WHILE the boundary is showing its fallback, the
   * error state clears automatically (e.g. a caller passes the current route
   * so navigating away from a crashed page recovers it). Does NOT force a
   * remount and has no effect while healthy — pass the route/location string
   * here rather than using it as a React `key`, which would remount (and
   * refetch) the wrapped subtree on every value change, including same-page
   * navigations (tab switches, `?query` changes) that never errored.
   */
  readonly resetKey?: unknown;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep a console trace for debugging; never swallow silently.
    console.error("[ErrorBoundary] caught render error:", error, info.componentStack);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Auto-clear ONLY while erroring — a healthy boundary ignores resetKey
    // changes entirely, so ordinary re-renders (including same-component
    // navigations like a dashboard tab switch) never touch this branch.
    if (this.state.error !== null && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    const { fallback, label } = this.props;
    if (typeof fallback === "function") return fallback(error, this.reset);
    if (fallback !== undefined) return fallback;

    return (
      <div className="pt-4" data-testid="error-boundary-fallback">
        <div className="rounded-[14px] border border-border-default bg-bg-elevated p-6 text-center">
          <AlertTriangle className="mx-auto mb-3 h-7 w-7 text-warning" aria-hidden="true" />
          <p className="text-[14px] font-medium text-text-secondary">
            {describeError(error, { action: `load ${label ?? "this section"}` })}
          </p>
          <p className="mt-1 text-[13px] text-text-tertiary">
            The rest of the app is unaffected.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-border-default px-4 py-2 text-[13px] text-text-secondary hover:text-text-primary hover:border-strenn-emerald/30 transition-colors"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
            {/* A "Try again" re-render alone cannot recover from a stale
                lazy-loaded chunk (React caches the rejected import() promise
                and re-throws it on retry) — the most likely real-world
                trigger for a page-content crash after a deploy. A real
                reload always recovers. */}
            {/* Neutral, not emerald (CLAUDE.md Design Constraints — emerald is
                reserved for success/hardware-confirmed/AAL-milestone states;
                a reload button is neither). */}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-border-default px-4 py-2 text-[13px] text-text-secondary hover:text-text-primary hover:border-interactive/40 transition-colors"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
